import { type AdminDeviceSession } from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut, Smartphone } from 'lucide-react';
import { useId, useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import { fetchMobileSessions, revokeAllMobileSessions, revokeMobileSession } from './api';

const QUERY_KEY = 'admin-mobile-sessions';

/** One user's device sessions, active first, most recently seen first. */
export interface DeviceGroup {
  userId: string;
  sessions: AdminDeviceSession[];
  activeCount: number;
}

const byRecency = (a: AdminDeviceSession, b: AdminDeviceSession): number => {
  if (a.active !== b.active) return a.active ? -1 : 1;
  return Date.parse(b.lastSeenAt) - Date.parse(a.lastSeenAt);
};

/**
 * Groups sessions by user. A user with an active device sorts above one with
 * only revoked ones; inside each bucket the most recent activity wins.
 */
export function groupByUser(sessions: readonly AdminDeviceSession[]): DeviceGroup[] {
  const map = new Map<string, AdminDeviceSession[]>();
  for (const session of sessions) {
    const list = map.get(session.userId);
    if (list) list.push(session);
    else map.set(session.userId, [session]);
  }
  return [...map.entries()]
    .map(([userId, list]) => {
      const sorted = [...list].sort(byRecency);
      return { userId, sessions: sorted, activeCount: sorted.filter((s) => s.active).length };
    })
    .sort((a, b) => {
      if ((a.activeCount > 0) !== (b.activeCount > 0)) return a.activeCount > 0 ? -1 : 1;
      return byRecency(a.sessions[0]!, b.sessions[0]!);
    });
}

const DATE_TIME = new Intl.DateTimeFormat('uk-UA', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : DATE_TIME.format(date);
};

const deviceName = (session: AdminDeviceSession): string =>
  `${session.model} · ${t(`adminMobileDevices.platform.${session.platform}`)}`;

type RevokeTarget =
  | { kind: 'one'; session: AdminDeviceSession }
  | { kind: 'all'; userId: string; count: number };

/**
 * «Мобільні пристрої» — SUPERUSER only (admin-mobile-app phase 6, SC-10, D-4).
 *
 * Every admin-app device session the API holds — active, and revoked ones until
 * their 90-day TTL — grouped by user. «Відкликати» ends one device session,
 * «Вийти скрізь» ends every active one of a user; both confirm first through
 * the shared ConfirmDialog (focus on confirm, Escape closes), and both refetch
 * the list on success. The API answers 403 to a plain admin as well, which
 * renders <AccessDenied /> in case the route gate is ever bypassed.
 */
export function MobileDevicesPage() {
  const queryClient = useQueryClient();
  const filterId = useId();
  const [activeOnly, setActiveOnly] = useState(false);
  const [target, setTarget] = useState<RevokeTarget | null>(null);

  const sessionsQuery = useQuery({
    queryKey: [QUERY_KEY, { activeOnly }],
    queryFn: () => fetchMobileSessions({ activeOnly }),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeMobileSession(id),
    onSuccess: () => {
      toast.success(t('adminMobileDevices.toast.revoked'));
      void invalidate();
    },
    onError: () => toast.error(t('adminMobileDevices.errors.revokeFailed')),
    onSettled: () => setTarget(null),
  });

  const revokeAllMutation = useMutation({
    mutationFn: (userId: string) => revokeAllMobileSessions(userId),
    onSuccess: (result) => {
      toast.success(t('adminMobileDevices.toast.revokedAll', { count: result.revoked }));
      void invalidate();
    },
    onError: () => toast.error(t('adminMobileDevices.errors.revokeAllFailed')),
    onSettled: () => setTarget(null),
  });

  const error = sessionsQuery.error;
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return <AccessDenied />;
  }

  const sessions = sessionsQuery.data ?? [];
  const groups = groupByUser(sessions);
  const activeTotal = sessions.filter((s) => s.active).length;
  const busy = revokeMutation.isPending || revokeAllMutation.isPending;

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
            {t('adminMobileDevices.title')}
          </h1>
          <p className="mt-1.5 max-w-2xl text-[13px] leading-[1.5] text-cf-ink-muted">
            {t('adminMobileDevices.subtitle')}
          </p>
          {sessionsQuery.isSuccess && (
            <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:text-[11px]">
              {t('adminMobileDevices.eyebrow', { active: activeTotal, users: groups.length })}
            </p>
          )}
        </div>
        <label
          htmlFor={filterId}
          className="flex h-10 cursor-pointer items-center gap-2 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-sm font-semibold"
        >
          <input
            id={filterId}
            type="checkbox"
            checked={activeOnly}
            onChange={(event) => setActiveOnly(event.target.checked)}
            className="h-4 w-4 accent-cf-brand"
          />
          {t('adminMobileDevices.activeOnly')}
        </label>
      </div>

      {sessionsQuery.isLoading ? (
        <Placeholder text={t('adminMobileDevices.loading')} status />
      ) : sessionsQuery.isError ? (
        <Placeholder text={t('adminMobileDevices.errors.failedToLoad')} alert />
      ) : groups.length === 0 ? (
        <Placeholder
          text={activeOnly ? t('adminMobileDevices.emptyActive') : t('adminMobileDevices.empty')}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <UserGroup
              key={group.userId}
              group={group}
              busy={busy}
              onRevoke={(session) => setTarget({ kind: 'one', session })}
              onRevokeAll={() =>
                setTarget({ kind: 'all', userId: group.userId, count: group.activeCount })
              }
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={target !== null}
        tone="danger"
        title={
          target?.kind === 'all'
            ? t('adminMobileDevices.confirm.revokeAllTitle')
            : t('adminMobileDevices.confirm.revokeTitle')
        }
        message={
          target?.kind === 'all'
            ? t('adminMobileDevices.confirm.revokeAllMessage', {
                user: target.userId,
                count: target.count,
              })
            : target
              ? t('adminMobileDevices.confirm.revokeMessage', { device: deviceName(target.session) })
              : ''
        }
        confirmLabel={
          target?.kind === 'all'
            ? t('adminMobileDevices.actions.revokeAll')
            : t('adminMobileDevices.actions.revoke')
        }
        busy={busy}
        onCancel={() => setTarget(null)}
        onConfirm={() => {
          if (!target) return;
          if (target.kind === 'all') revokeAllMutation.mutate(target.userId);
          else revokeMutation.mutate(target.session.id);
        }}
      />
    </>
  );
}

const GRID =
  'lg:grid lg:min-w-[900px] lg:grid-cols-[minmax(0,1.6fr)_90px_80px_150px_130px_minmax(0,1.3fr)_110px] lg:items-center lg:gap-4 lg:px-[18px]';

function UserGroup({
  group,
  busy,
  onRevoke,
  onRevokeAll,
}: {
  group: DeviceGroup;
  busy: boolean;
  onRevoke: (session: AdminDeviceSession) => void;
  onRevokeAll: () => void;
}) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      data-user-id={group.userId}
      className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-cf-line-soft px-3.5 py-3 lg:px-[18px]">
        <div className="min-w-0">
          <span className="block font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase">
            {t('adminMobileDevices.user.label')}
          </span>
          <h2 id={headingId} className="truncate font-mono text-sm font-semibold">
            {group.userId}
          </h2>
          <span className="text-xs text-cf-ink-subtle">
            {t('adminMobileDevices.user.summary', {
              active: group.activeCount,
              total: group.sessions.length,
            })}
          </span>
        </div>
        {group.activeCount > 0 && (
          <button
            type="button"
            onClick={onRevokeAll}
            disabled={busy}
            aria-label={t('adminMobileDevices.actions.revokeAllAria', { user: group.userId })}
            className="flex h-9 items-center gap-2 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-xs font-semibold text-cf-danger hover:border-cf-ink disabled:opacity-50"
          >
            <LogOut size={14} strokeWidth={1.75} aria-hidden />
            {t('adminMobileDevices.actions.revokeAll')}
          </button>
        )}
      </header>

      <div className="lg:overflow-x-auto">
        <div
          className={`hidden ${GRID} lg:border-b lg:border-cf-line-soft lg:py-2.5 lg:font-mono lg:text-[10px] lg:tracking-[0.12em] lg:text-cf-ink-subtle lg:uppercase`}
        >
          <span>{t('adminMobileDevices.table.device')}</span>
          <span>{t('adminMobileDevices.table.platform')}</span>
          <span>{t('adminMobileDevices.table.version')}</span>
          <span>{t('adminMobileDevices.table.lastSeen')}</span>
          <span>{t('adminMobileDevices.table.ip')}</span>
          <span>{t('adminMobileDevices.table.status')}</span>
          <span />
        </div>
        <ul>
          {group.sessions.map((session) => (
            <SessionRow
              key={session.id}
              session={session}
              busy={busy}
              onRevoke={() => onRevoke(session)}
            />
          ))}
        </ul>
      </div>
    </section>
  );
}

function SessionRow({
  session,
  busy,
  onRevoke,
}: {
  session: AdminDeviceSession;
  busy: boolean;
  onRevoke: () => void;
}) {
  const status = session.active
    ? t('adminMobileDevices.table.active')
    : t('adminMobileDevices.table.revoked');
  const reason = session.revokeReason ? t(`adminMobileDevices.reason.${session.revokeReason}`) : null;
  const badge = session.active
    ? 'bg-cf-success-tint text-cf-success'
    : 'bg-cf-fill text-cf-ink-subtle';
  const platform = t(`adminMobileDevices.platform.${session.platform}`);
  const lastSeen = formatDateTime(session.lastSeenAt);
  const ip = session.lastIp ?? t('adminMobileDevices.table.noIp');

  return (
    <li
      data-session-id={session.id}
      className={`flex flex-col gap-2 border-b border-cf-line-soft px-3.5 py-3 last:border-b-0 ${GRID} lg:py-3 lg:text-[13px]`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Smartphone size={16} strokeWidth={1.75} className="shrink-0 text-cf-ink-subtle" aria-hidden />
        <div className="min-w-0">
          <div className="truncate font-semibold" data-field="model">
            {session.model}
          </div>
          <div className="truncate font-mono text-xs text-cf-ink-subtle lg:hidden">
            {platform} · v{session.appVersion} · {lastSeen} · {ip}
          </div>
        </div>
      </div>
      <span className="hidden lg:inline" data-field="platform">
        {platform}
      </span>
      <span className="hidden font-mono lg:inline" data-field="version">
        {session.appVersion}
      </span>
      <span className="hidden font-mono text-xs text-cf-ink-subtle lg:inline" data-field="last-seen">
        {lastSeen}
      </span>
      <span className="hidden truncate font-mono text-xs text-cf-ink-subtle lg:inline" data-field="ip">
        {ip}
      </span>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span
          data-field="status"
          className={`inline-flex w-fit rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] whitespace-nowrap uppercase ${badge}`}
        >
          {status}
        </span>
        {reason && (
          <span data-field="reason" className="text-xs text-cf-ink-muted">
            {reason}
          </span>
        )}
      </div>
      {session.active ? (
        <button
          type="button"
          onClick={onRevoke}
          disabled={busy}
          aria-label={t('adminMobileDevices.actions.revokeAria', { device: deviceName(session) })}
          className="h-8 w-fit rounded-cf-md border border-cf-line bg-cf-surface px-2.5 text-xs font-semibold text-cf-danger hover:border-cf-ink disabled:opacity-50 lg:justify-self-end"
        >
          {t('adminMobileDevices.actions.revoke')}
        </button>
      ) : (
        <span className="hidden lg:inline" />
      )}
    </li>
  );
}

function Placeholder({
  text,
  status = false,
  alert = false,
}: {
  text: string;
  status?: boolean;
  alert?: boolean;
}) {
  return (
    <section
      role={alert ? 'alert' : status ? 'status' : undefined}
      className="rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-10 text-center text-[13px] text-cf-ink-subtle lg:px-[18px]"
    >
      {text}
    </section>
  );
}
